/* One run's masked breaks, in a drawer over the definition they belong to.
 *
 * **A drawer rather than a page**, as the design draws it: a break list is read beside the runs it
 * came from, and a reader comparing two runs opens one, glances, closes it and opens the next. The
 * address still exists — `/reconciliation/:definitionId/runs/:runId` opens the definition with this
 * drawer over it — so a break list somebody was sent still opens.
 *
 * **Every row is masked, and the drawer says so in its subtitle.** A break carries a masked key, a
 * digest computed at the source, the names of the fields that disagreed and a numeric distance —
 * never a value. Investigating the real rows happens at the customer's own source, through the
 * statements the investigation panel writes; Lumiq never holds them.
 *
 * **The chip counts are the loaded rows, never the run's totals.** The list is capped at the source,
 * and a chip promising 469 rows a click cannot produce is worse than no chip — so the totals are said
 * once, in the line under the chips, beside how many were loaded.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ageText } from '@/shared/model/age';
import { compactNumber } from '@/shared/model/number';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { HelpLabel } from '@/shared/ui/InfoTip';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import { SidePanel } from '@/shared/ui/SidePanel';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useBreaks, useRun } from '../api/reconciliation';
import {
  BREAK_FILTERS,
  breakCounts,
  breakKindLabelKey,
  breaksToCsv,
  breakToneOf,
  breakTotalOf,
  filterBreaks,
  formatDelta,
  type BreakFilter,
} from '../model/reconciliation';
import { InvestigationPanel } from './InvestigationPanel';

/** How much of a digest a row shows. Enough to tell two keys apart by eye; the whole digest is on
 * hover, for whoever is matching a break against another by hand. */
const DIGEST_VISIBLE = 8;

export function BreaksDrawer({
  definitionId,
  runId,
  definitionName,
  onClose,
}: {
  readonly definitionId: string;
  readonly runId: string;
  readonly definitionName: string;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const run = useRun(definitionId, runId);
  const breaks = useBreaks(definitionId, runId);
  const [filter, setFilter] = useState<BreakFilter>('all');
  const [exported, setExported] = useState<number | null>(null);
  const [investigating, setInvestigating] = useState(false);

  const loaded = breaks.isSuccess ? breaks.data.items : [];
  const counts = breakCounts(loaded);
  const shown = filterBreaks(loaded, filter);
  const total = run.isSuccess ? breakTotalOf(run.data) : null;
  const truncated = run.isSuccess && run.data.breaks_truncated;
  const when = run.isSuccess
    ? (ageText(t, run.data.requested_at, new Date()) ?? run.data.requested_at)
    : null;

  const head = (
    <>
      <HelpLabel subject={t('reconciliation.breaks.title')} help={t('help.term.break')}>
        <h2 className="lq-side__name">
          {when === null
            ? t('reconciliation.breaks.title')
            : t('reconciliation.breaks.drawerTitle', { when })}
        </h2>
      </HelpLabel>
      <div className="lq-side__sub">{t('reconciliation.breaks.masked')}</div>
    </>
  );

  return (
    <>
      <SidePanel
        label={t('reconciliation.breaks.title')}
        className="lq-side--wide"
        head={head}
        onClose={() => {
          /* Escape reaches both the investigation dialog and this drawer. The dialog closes itself;
             the drawer stays, because the reader pressed Escape to leave the dialog. */
          if (!investigating) onClose();
        }}
      >
        <div className="lq-bkd">
          {breaks.isPending && <SkeletonRows rows={5} columns={2} />}

          {breaks.isError && (
            <Alert tone="error" title={t('reconciliation.breaks.failed')}>
              {breaks.error.message}
            </Alert>
          )}

          {breaks.isSuccess && loaded.length === 0 && (
            <p className="lq-bkd__empty">{t('reconciliation.breaks.empty.title')}</p>
          )}

          {breaks.isSuccess && loaded.length > 0 && (
            <>
              <SegmentedFilter
                label={t('reconciliation.breaks.filterAria')}
                value={filter}
                onChange={setFilter}
                options={BREAK_FILTERS.map((kind) => ({
                  id: kind,
                  label:
                    kind === 'all'
                      ? t('reconciliation.breaks.filter.all')
                      : t(breakKindLabelKey(kind)),
                  count: kind === 'all' ? loaded.length : counts[kind],
                }))}
              />

              <p className="lq-bkd__loaded">
                {truncated || (total !== null && loaded.length < total)
                  ? t('reconciliation.breaks.loaded.some', {
                      loaded: loaded.length,
                      total: total === null ? '' : compactNumber(total),
                    })
                  : t('reconciliation.breaks.loaded.all', { count: loaded.length })}
              </p>

              <div className="lq-bkd__actions">
                <Button
                  size="compact"
                  disabled={shown.length === 0}
                  onClick={() => {
                    downloadCsv(
                      `${definitionName}-${runId}-breaks.csv`,
                      breaksToCsv(shown),
                    );
                    setExported(shown.length);
                  }}
                >
                  {t('reconciliation.breaks.exportCsv')}
                </Button>
                <Button
                  size="compact"
                  onClick={() => {
                    setInvestigating(true);
                  }}
                >
                  {t('reconciliation.breaks.investigate')}
                </Button>
              </div>

              {exported !== null && (
                <p className="lq-bkd__flash" role="status">
                  {t(
                    truncated
                      ? 'reconciliation.breaks.exportedSample'
                      : 'reconciliation.breaks.exportedAll',
                    { count: exported },
                  )}
                </p>
              )}

              {shown.length === 0 ? (
                <p className="lq-bkd__empty">
                  {t('reconciliation.breaks.noneOfKind.title')}
                </p>
              ) : (
                <ul
                  className="lq-bkd__list"
                  aria-label={t('reconciliation.breaks.caption', {
                    name: definitionName,
                  })}
                >
                  {shown.map((item) => {
                    const delta = formatDelta(item.numeric_delta);
                    return (
                      <li key={item.id} className="lq-bkd__row">
                        <span className="lq-bkd__key lq-mono">
                          {item.masked_key}{' '}
                          <span className="lq-bkd__digest" title={item.key_digest}>
                            {item.key_digest.slice(0, DIGEST_VISIBLE)}…
                          </span>
                        </span>
                        <span
                          className={`lq-bkd__kind lq-bkd__kind--${breakToneOf(item.kind)}`}
                        >
                          {t(breakKindLabelKey(item.kind))}
                        </span>
                        <span
                          className="lq-bkd__fields"
                          title={
                            item.fields.length === 0
                              ? t('reconciliation.breaks.noFieldsWhy')
                              : undefined
                          }
                        >
                          {t('reconciliation.breaks.fields', {
                            fields:
                              item.fields.length === 0
                                ? t('reconciliation.breaks.noFields')
                                : item.fields.join(', '),
                          })}
                        </span>
                        <span className="lq-bkd__delta">
                          {t('reconciliation.breaks.delta', {
                            delta: delta ?? t('reconciliation.breaks.noDelta'),
                          })}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>
      </SidePanel>

      <InvestigationPanel
        open={investigating}
        onClose={() => {
          setInvestigating(false);
        }}
        definitionId={definitionId}
        runId={runId}
        fileStem={definitionName}
      />
    </>
  );
}

function downloadCsv(filename: string, body: string): void {
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
